import React, { useState, useEffect, useRef } from 'react';
import { WRAP, SECTION_PAD } from '../styles/layout';

/* ═══════════════════════════════════════════════════════════════════════════
   HOW IT WORKS  (v2 — mobile/tablet responsive)
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Breakpoint hook ──────────────────────────────────────────────────── */
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return { mob: w < 768, tab: w < 1100 };
}

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

/* ── Agent data ───────────────────────────────────────────────────────── */
const AGENTS = [
  {
    id: 'architect', tag: 'AI Architect', color: '#00D4AA', rgb: '0,212,170',
    title: 'Modernise your legacy apps',
    problem: "Your apps can't connect to AI agents",
    what: 'Analyses your legacy stack — PowerBuilder, Oracle Forms, VB6, Classic ASP — and re-architects it with modern APIs, AI integration points, and pgvector support.',
    handoffTo: 'ciso',
    handoffLabel: 'Passes modernised app architecture to AI CISO for security posturing',
    outcome: 'AI-agent ready applications',
    outcomeDetail: 'Every app can now connect to, receive tasks from, and return results to AI agents.',
    timing: '6–16 weeks', standalone: true,
    standaloneNote: 'Can run standalone — modernises apps without other agents.',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="3" width="6" height="6" rx="1"/><rect x="16" y="3" width="6" height="6" rx="1"/><rect x="9" y="15" width="6" height="6" rx="1"/><path d="M5 9v3h14V9"/><path d="M12 12v3"/></svg>,
  },
  {
    id: 'ciso', tag: 'AI CISO', color: '#10B981', rgb: '16,185,129',
    title: 'Secure and govern your AI',
    problem: 'No visibility, no governance, no compliance path',
    what: 'Discovers every AI agent and tool across your org. Deploys AI Agent IAM, immutable audit trails, and policy templates covering EU AI Act, SOC2, and HIPAA.',
    handoffTo: 'dba',
    handoffLabel: 'Shares compliance posture and data-handling policies with AI DBA before migration',
    outcome: 'Governed, compliant AI environment',
    outcomeDetail: 'Board-ready compliance evidence, automatic policy enforcement, full AI visibility.',
    timing: '48 hrs to full visibility', standalone: true,
    standaloneNote: 'Can run standalone — governs your AI environment independently.',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>,
  },
  {
    id: 'dba', tag: 'AI DBA', color: '#F59E0B', rgb: '245,158,11',
    title: 'Migrate your database',
    problem: 'Oracle, SQL Server, Sybase costs are unsustainable',
    what: 'Migrates from Oracle, SQL Server, and Sybase to PostgreSQL 16. Inherits data-handling compliance rules from AI CISO — no re-mapping policies post-migration.',
    handoffTo: 'qa',
    handoffLabel: 'Hands migrated schema and data to AI QA for validation',
    outcome: '50%+ database cost reduction',
    outcomeDetail: 'PostgreSQL on modern infrastructure, compliant from day one.',
    timing: '4–12 weeks', standalone: true,
    standaloneNote: 'Can run standalone — migrates your database without other agents.',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/></svg>,
  },
  {
    id: 'qa', tag: 'AI QA', color: '#8B5CF6', rgb: '139,92,246',
    title: 'Validate everything that changed',
    problem: 'Migrations and modernisations break things',
    what: 'Runs comprehensive test suites across every changed system — app APIs, migrated data, new integrations, security policies. Catches regressions before they hit production.',
    handoffTo: null, handoffLabel: null,
    outcome: 'Zero missed regressions',
    outcomeDetail: 'Every change validated. Ship faster, break nothing.',
    timing: '80% faster test cycles', standalone: true,
    standaloneNote: 'Can run standalone — tests any system, not just RevUp-built ones.',
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>,
  },
];

const SCENARIOS = [
  { label: 'Need to modernise apps',       agents: ['architect','ciso','qa'],              start: 'architect', description: 'Start with AI Architect. It modernises your apps and hands off to AI CISO which secures the new architecture. AI QA validates the full stack.' },
  { label: 'Need to cut database costs',   agents: ['ciso','dba','qa'],                    start: 'dba',       description: 'Start with AI DBA. AI CISO runs first to define data-handling policies, DBA migrates with those rules already applied, AI QA validates the migration.' },
  { label: 'Need AI governance now',       agents: ['ciso'],                               start: 'ciso',      description: "Start with AI CISO only. Full AI visibility in 48 hours. Governance and compliance running. Add other agents when you're ready." },
  { label: 'Need the full transformation', agents: ['architect','ciso','dba','qa'],        start: 'architect', description: 'All four agents activated. Sequential handoffs across the full stack — apps modernised, databases migrated, environment governed, everything validated.' },
];

/* ── Agent node ───────────────────────────────────────────────────────── */
function AgentNode({ agent, isActive, isInScenario, onClick, showConnector, inView, index, mob }) {
  const [hov, setHov] = useState(false);
  const active = isActive || isInScenario;

  return (
    <div style={{ display:'flex', alignItems:'center', flex: mob ? '1 1 100%' : '1', minWidth:0 }}>
      <div
        onClick={() => onClick(agent.id)}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          flex:1, minWidth:0, position:'relative', overflow:'hidden',
          borderRadius:14, padding:'22px 20px',
          background: active ? `rgba(${agent.rgb},0.09)` : (hov ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)'),
          border: `1px solid ${active ? `rgba(${agent.rgb},0.4)` : (hov ? 'rgba(200,192,236,0.25)' : 'rgba(255,255,255,0.06)')}`,
          cursor:'pointer', transition:'all .25s ease',
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(20px)',
          transitionDelay: `${index*0.1}s`,
          boxShadow: active ? `0 0 32px rgba(${agent.rgb},0.1)` : 'none',
        }}
      >
        <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,transparent,${agent.color},transparent)`, opacity: active?1:(hov?0.5:0.15), transition:'opacity .25s' }}/>
        {active && <div style={{ position:'absolute', top:12, right:12, width:8, height:8, borderRadius:'50%', background:agent.color, boxShadow:`0 0 8px ${agent.color}`, animation:'pulse 2s ease-in-out infinite' }}/>}
        <div style={{ width:40, height:40, borderRadius:11, marginBottom:14, background:`rgba(${agent.rgb},${active?'0.15':'0.08'})`, border:`1px solid rgba(${agent.rgb},${active?'0.4':'0.2'})`, display:'flex', alignItems:'center', justifyContent:'center', color:agent.color, transition:'all .25s' }}>{agent.icon}</div>
        <div style={{ fontSize:9, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:agent.color, opacity:.8, marginBottom:5 }}>{agent.tag}</div>
        <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:15, fontWeight:800, color:active?'#EDE9F8':'#DDD8F2', lineHeight:1.2, marginBottom:8, transition:'color .25s' }}>{agent.title}</div>
        <div style={{ fontSize:11, fontWeight:600, color:active?agent.color:'#9B90BA', transition:'color .25s' }}>{agent.timing}</div>
      </div>

      {/* Horizontal connector — desktop/tablet only */}
      {showConnector && !mob && (
        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', width:36, flexShrink:0, gap:4, opacity: inView?(isInScenario?0.8:0.2):0, transition:`opacity .4s ease ${index*0.1+0.3}s` }}>
          <div style={{ width:28, height:1, background: isInScenario ? `linear-gradient(90deg,${agent.color},${AGENTS[AGENTS.findIndex(a=>a.id===agent.id)+1]?.color||agent.color})` : 'rgba(255,255,255,0.2)', transition:'all .4s ease' }}/>
          <svg width="8" height="8" viewBox="0 0 8 8" fill={isInScenario?agent.color:'rgba(255,255,255,0.3)'}><polygon points="0,0 8,4 0,8"/></svg>
        </div>
      )}

      {/* Down arrow — mobile stacked view */}
      {showConnector && mob && (
        <div style={{ position:'absolute', left:'50%', transform:'translateX(-50%)', display:'flex', flexDirection:'column', alignItems:'center', opacity: inView?(isInScenario?0.8:0.2):0, transition:`opacity .4s ease ${index*0.1+0.3}s`, marginTop:4 }}>
          <div style={{ width:1, height:16, background: isInScenario?agent.color:'rgba(255,255,255,0.2)' }}/>
          <svg width="8" height="8" viewBox="0 0 8 8" fill={isInScenario?agent.color:'rgba(255,255,255,0.3)'} style={{ transform:'rotate(90deg)' }}><polygon points="0,0 8,4 0,8"/></svg>
        </div>
      )}
    </div>
  );
}

/* ── Detail panel ─────────────────────────────────────────────────────── */
function DetailPanel({ agent, scenario, inView, mob, tab }) {
  if (!agent) return (
    <div style={{ borderRadius:16, padding:'32px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', display:'flex', alignItems:'center', justifyContent:'center', minHeight:180, opacity:inView?1:0, transition:'opacity .5s ease' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:13, color:'rgba(255,255,255,0.2)', marginBottom:8 }}>{mob ? '↑ Select an agent above' : '↑ Select an agent or scenario above'}</div>
        <div style={{ fontSize:12, color:'rgba(200,192,236,0.25)' }}>See what each agent does, what it receives, and what it hands off</div>
      </div>
    </div>
  );

  const cols = mob ? '1fr' : tab ? '1fr 1fr' : '1fr 1fr 1fr';

  return (
    <div style={{ borderRadius:16, padding: mob?'24px':'32px', background:`rgba(${agent.rgb},0.05)`, border:`1px solid rgba(${agent.rgb},0.22)`, position:'relative', overflow:'hidden', opacity:inView?1:0, transition:'opacity .4s ease' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,transparent,${agent.color},transparent)` }}/>

      <div style={{ display:'grid', gridTemplateColumns: cols, gap: mob?'20px':'24px' }}>
        {/* What it does */}
        <div>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:agent.color, opacity:.7, marginBottom:10 }}>What {agent.tag} does</div>
          <p style={{ fontSize:13.5, fontWeight:300, lineHeight:1.75, color:'#C8C0EC', margin:0 }}>{agent.what}</p>
        </div>

        {/* Handoff */}
        <div>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:agent.color, opacity:.7, marginBottom:10 }}>
            {agent.handoffTo ? 'Hands off to next agent' : 'Network output'}
          </div>
          {agent.handoffTo ? (
            <>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10, padding:'8px 12px', borderRadius:8, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                <span style={{ fontSize:12, fontWeight:600, color:`rgba(${AGENTS.find(a=>a.id===agent.handoffTo)?.rgb},0.9)` }}>{AGENTS.find(a=>a.id===agent.handoffTo)?.tag}</span>
              </div>
              <p style={{ fontSize:13, fontWeight:300, lineHeight:1.7, color:'#9B90BA', margin:0 }}>{agent.handoffLabel}</p>
            </>
          ) : (
            <p style={{ fontSize:13, fontWeight:300, lineHeight:1.7, color:'#9B90BA', margin:0 }}>Final agent in the chain. Results feed back into your environment as the validated, production-ready output.</p>
          )}
        </div>

        {/* Outcome */}
        <div style={{ padding:'20px', borderRadius:12, background:`rgba(${agent.rgb},0.07)`, border:`1px solid rgba(${agent.rgb},0.2)` }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:agent.color, opacity:.7, marginBottom:10 }}>Your outcome</div>
          <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:17, fontWeight:800, color:agent.color, marginBottom:8, lineHeight:1.2 }}>{agent.outcome}</div>
          <p style={{ fontSize:12.5, color:'#9B90BA', lineHeight:1.65, margin:'0 0 14px' }}>{agent.outcomeDetail}</p>
          <div style={{ padding:'6px 12px', borderRadius:100, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', display:'inline-block', fontSize:11, fontWeight:600, color:'#9B90BA' }}>
            {agent.standaloneNote}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══ MAIN SECTION ════════════════════════════════════════════════════════ */
export default function HowItWorks() {
  const { mob, tab } = useBreakpoint();
  const [headerRef,  headerInView]  = useInView(0.2);
  const [networkRef, networkInView] = useInView(0.08);

  const [activeAgent,    setActiveAgent]    = useState(null);
  const [activeScenario, setActiveScenario] = useState(null);

  const currentAgent   = AGENTS.find(a => a.id === activeAgent) || null;
  const scenarioAgents = activeScenario !== null ? SCENARIOS[activeScenario].agents : [];

  const handleAgentClick = (id) => {
    setActiveScenario(null);
    setActiveAgent(prev => prev === id ? null : id);
  };

  const handleScenarioClick = (i) => {
    setActiveAgent(null);
    setActiveScenario(prev => prev === i ? null : i);
    if (activeScenario !== i) setActiveAgent(SCENARIOS[i].start);
  };

  useEffect(() => {
    if (activeScenario !== null) setActiveAgent(SCENARIOS[activeScenario].start);
  }, [activeScenario]);

  return (
    <section style={{ position:'relative', padding:`${SECTION_PAD} 0`, overflow:'hidden' }}>

      {/* Atmosphere */}
      <div style={{ position:'absolute', inset:0, pointerEvents:'none' }}>
        <div style={{ position:'absolute', top:0, left:0, right:0, height:160, background:'linear-gradient(to bottom, #09060F, transparent)' }}/>
        <div style={{ position:'absolute', top:'25%', right:'5%', width:550, height:550, borderRadius:'50%', background:'radial-gradient(circle,rgba(0,212,170,0.04) 0%,transparent 65%)', filter:'blur(80px)' }}/>
        <div style={{ position:'absolute', bottom:'15%', left:'8%', width:480, height:480, borderRadius:'50%', background:'radial-gradient(circle,rgba(139,92,246,0.05) 0%,transparent 65%)', filter:'blur(80px)' }}/>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:160, background:'linear-gradient(to top, #09060F, transparent)' }}/>
      </div>

      <div style={{ ...WRAP, position:'relative', zIndex:2 }}>

        {/* ── Header ──────────────────────────────────────────────── */}
        <div ref={headerRef} style={{
          display:'grid',
          gridTemplateColumns: tab ? '1fr' : '1fr 1fr',
          gap: tab ? '36px' : '64px',
          alignItems:'end', marginBottom:64,
          opacity: headerInView?1:0,
          transform: headerInView?'translateY(0)':'translateY(20px)',
          transition:'opacity .7s ease, transform .7s ease',
        }}>
          <div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px 5px 8px', borderRadius:100, background:'rgba(0,212,170,0.08)', border:'1px solid rgba(0,212,170,0.2)', marginBottom:24 }}>
              <div style={{ width:18, height:18, borderRadius:'50%', background:'rgba(0,212,170,0.15)', border:'1px solid rgba(0,212,170,0.35)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <div style={{ width:6, height:6, borderRadius:'50%', background:'#00D4AA', animation:'pulse 2s ease-in-out infinite' }}/>
              </div>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:'#00D4AA' }}>RevUp Agent Network</span>
            </div>

            <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:'clamp(34px,4vw,54px)', fontWeight:900, letterSpacing:'-1.5px', lineHeight:1.05, marginBottom:18, color:'#EDE9F8' }}>
              Start with the problem{' '}
              <span style={{ color:'#9B90BA', fontStyle:'italic', fontWeight:300 }}>you have today.</span>
            </h2>

            <p style={{ fontSize:16, fontWeight:300, lineHeight:1.78, color:'#9B90BA', margin:0 }}>
              <span style={{ color:'#DDD8F2', fontWeight:500 }}>Plug in the agent you need. The network handles the rest.</span>{' '}
              Each RevUp agent is fully standalone — but when you need more, they hand off to each other automatically. One modernised app becomes a governed, validated, AI-ready system without you coordinating a single handoff.
            </p>
          </div>

          {/* Stat cards — 2×2, stays at any width */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            {[
              { val:'4',       label:'Connected AI agents',      color:'#00D4AA' },
              { val:'Day 1',   label:'Value from first agent',   color:'#F59E0B' },
              { val:'Auto',    label:'Sequential handoffs',      color:'#8B5CF6' },
              { val:'Modular', label:'Start with one, scale up', color:'#10B981' },
            ].map((s,i) => (
              <div key={i} style={{ padding:'18px 20px', borderRadius:12, background:'rgba(255,255,255,0.025)', border:'1px solid rgba(255,255,255,0.07)', opacity:headerInView?1:0, transform:headerInView?'translateY(0)':'translateY(12px)', transition:`opacity .5s ease ${0.2+i*0.08}s, transform .5s ease ${0.2+i*0.08}s` }}>
                <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:28, fontWeight:900, color:s.color, lineHeight:1, marginBottom:5 }}>{s.val}</div>
                <div style={{ fontSize:12, color:'#9B90BA', lineHeight:1.4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Network diagram ──────────────────────────────────────── */}
        <div ref={networkRef}>

          {/* Scenario selector */}
          <div style={{ marginBottom:28 }}>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:'rgba(200,192,236,0.5)', marginBottom:12 }}>
              {mob ? 'Try a scenario' : 'Try a scenario — or click any agent below'}
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {SCENARIOS.map((s,i) => (
                <button key={i} onClick={() => handleScenarioClick(i)} style={{
                  background: activeScenario===i ? 'rgba(0,212,170,0.12)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${activeScenario===i ? 'rgba(0,212,170,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  color: activeScenario===i ? '#00D4AA' : '#9B90BA',
                  cursor:'pointer', padding:'8px 16px', borderRadius:100,
                  fontSize: mob ? 12 : 13, fontWeight:500,
                  fontFamily:"'DM Sans',system-ui,sans-serif",
                  transition:'all .2s ease', whiteSpace:'nowrap',
                }}
                  onMouseEnter={e=>{if(activeScenario!==i){e.currentTarget.style.borderColor='rgba(255,255,255,0.2)';e.currentTarget.style.color='#DDD8F2';}}}
                  onMouseLeave={e=>{if(activeScenario!==i){e.currentTarget.style.borderColor='rgba(255,255,255,0.1)';e.currentTarget.style.color='#9B90BA';}}}
                >
                  {/* Abbreviate labels on mobile */}
                  {mob ? s.label.replace('Need to ','').replace('Need ','') : s.label}
                </button>
              ))}
            </div>
            {activeScenario !== null && (
              <div style={{ marginTop:14, padding:'12px 16px', borderRadius:10, background:'rgba(0,212,170,0.05)', border:'1px solid rgba(0,212,170,0.15)', fontSize:13.5, color:'#C8C0EC', lineHeight:1.65 }}>
                <span style={{ color:'#00D4AA', fontWeight:600 }}>How it works: </span>
                {SCENARIOS[activeScenario].description}
              </div>
            )}
          </div>

          {/* Agent nodes — row on desktop/tablet, stacked column on mobile */}
          <div style={{ display:'flex', flexDirection: mob?'column':'row', gap: mob?0:0, marginBottom:20, alignItems:'stretch', position: mob?'relative':'static' }}>
            {AGENTS.map((agent,i) => (
              <AgentNode
                key={agent.id} agent={agent}
                isActive={activeAgent === agent.id}
                isInScenario={scenarioAgents.includes(agent.id)}
                onClick={handleAgentClick}
                showConnector={i < AGENTS.length - 1}
                inView={networkInView} index={i} mob={mob}
              />
            ))}
          </div>

          {/* Detail panel */}
          <DetailPanel
            agent={currentAgent}
            scenario={activeScenario !== null ? SCENARIOS[activeScenario] : null}
            inView={networkInView} mob={mob} tab={tab}
          />

          {/* Network note */}
          <div style={{ marginTop:20, display:'flex', alignItems:'center', gap:16, padding: mob?'14px 16px':'14px 20px', borderRadius:12, background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.05)', opacity:networkInView?1:0, transition:'opacity .5s ease .6s' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(0,212,170,0.6)" strokeWidth="2" style={{ flexShrink:0 }}>
              <circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/>
            </svg>
            <span style={{ fontSize:13, color:'#9B90BA', lineHeight:1.6 }}>
              <span style={{ color:'#C8C0EC', fontWeight:500 }}>Every agent is standalone and fully operational from day one.</span>{' '}
              When multiple agents run together, handoffs are automatic — no integration work on your side. RevUp operators manage the coordination.
            </span>
          </div>
        </div>

        {/* ── Bottom CTA ───────────────────────────────────────────── */}
        <div style={{
          marginTop:72, display:'flex', alignItems:'center',
          flexDirection: mob ? 'column' : 'row',
          justifyContent:'space-between', flexWrap:'wrap', gap:20,
          padding: mob?'24px':'28px 36px', borderRadius:16,
          background:'rgba(0,212,170,0.04)', border:'1px solid rgba(0,212,170,0.12)',
          opacity:networkInView?1:0, transition:'opacity .6s ease .7s',
        }}>
          <div style={{ textAlign: mob?'center':'left' }}>
            <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize: mob?18:20, fontWeight:700, color:'#D4CEF0', marginBottom:6 }}>
              Which agent solves your biggest problem today?
            </div>
            <div style={{ fontSize:14, color:'#9B90BA' }}>Start with one. The network is ready when you need more.</div>
          </div>
          <button style={{
            background:'#00D4AA', color:'#09060F', border:'none', cursor:'pointer',
            padding:'13px 28px', borderRadius:9, fontSize:15, fontWeight:600,
            fontFamily:"'DM Sans',system-ui,sans-serif",
            boxShadow:'0 0 28px rgba(0,212,170,0.27)', transition:'all .25s ease', whiteSpace:'nowrap',
            width: mob ? '100%' : 'auto',
          }}
            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 0 44px rgba(0,212,170,0.5)';}}
            onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 0 28px rgba(0,212,170,0.27)';}}
          >
            Talk to Us About Your Stack →
          </button>
        </div>

      </div>

      <style>{`@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.4;transform:scale(.8)}}`}</style>
    </section>
  );
}